import { fetchAfImageUrls } from "@/lib/api/handlers/AfImageHandling";

/**
 * Batched, cached resolver for IMAGE additional-field thumbnail URLs.
 *
 * Backend: POST /api/v1/event/{eventId}/af-images/urls with
 * `{requests:[{guestId, fieldName}]}` → `{urls:{"<guestId>:<fieldName>": url}}`
 * (presigned GET, 300s TTL, entries without a stored value are skipped).
 *
 * Two layers keep a table full of thumbnails from stampeding the endpoint:
 *  - a module-level TTL cache (280s < the 300s presign TTL so a cached URL is
 *    never handed out already-expired), keyed `${eventId}:${guestId}:${field}`;
 *  - per-render batching: every request made in the same microtask tick is
 *    collected into ONE backend call (a guest-list render mounts dozens of
 *    TCCImage cells synchronously — they all ride one POST). The backend caps
 *    a single POST at 200 requests, so bigger ticks are split into sequential
 *    chunked POSTs of 200 — every queued request still resolves.
 */

const URL_TTL_MS = 280_000;
/** Error results get a SHORT negative-cache so a transient backend hiccup
 *  doesn't blank thumbnails for the full 280s window; a confirmed "guest has
 *  no value" answer keeps the long TTL. */
const ERROR_TTL_MS = 30_000;
/** Backend batch cap (spec: 200 per request) — bigger ticks are chunked. */
const MAX_BATCH = 200;
/** A lightbox-sized render wants a URL with meaningful presign life left;
 *  beyond this age (fetched >250s ago, <30s of the 280s window remaining) we
 *  re-resolve instead of racing the expiry. */
const FRESH_MAX_AGE_MS = 250_000;

type CacheEntry = { url: string | null; exp: number; fetchedAt: number };

const cache = new Map<string, CacheEntry>();

/** Per-key invalidation epoch: bumped by {@link invalidateAfImageUrl} so an
 *  in-flight batch that started BEFORE the invalidation never writes the key
 *  back (the fetched URL predates the upload/remove that invalidated it). */
const invalidationEpochs = new Map<string, number>();

type PendingRequest = {
  eventId: string;
  guestId: string;
  fieldName: string;
  resolve: (url: string | null) => void;
};

let pending: PendingRequest[] = [];
let flushScheduled = false;

function cacheKey(eventId: string, guestId: string, fieldName: string): string {
  return `${eventId}:${guestId}:${fieldName}`;
}

async function flush(): Promise<void> {
  flushScheduled = false;
  const batchAll = pending;
  pending = [];
  if (batchAll.length === 0) return;

  // Group by event (the endpoint is event-scoped).
  const byEvent = new Map<string, PendingRequest[]>();
  for (const req of batchAll) {
    const list = byEvent.get(req.eventId) ?? [];
    list.push(req);
    byEvent.set(req.eventId, list);
  }

  await Promise.all(
    Array.from(byEvent.entries()).map(async ([eventId, reqs]) => {
      // Dedupe identical guest/field pairs within the batch.
      const uniq = new Map<string, { guestId: string; fieldName: string }>();
      for (const r of reqs) {
        uniq.set(`${r.guestId}:${r.fieldName}`, {
          guestId: r.guestId,
          fieldName: r.fieldName,
        });
      }
      const entries = Array.from(uniq.values());
      // Snapshot invalidation epochs BEFORE the network round-trips: a key
      // invalidated mid-flight (upload landed while we were fetching) must not
      // be written back with the pre-upload URL.
      const epochSnapshot = new Map<string, number>();
      for (const e of entries) {
        const k = cacheKey(eventId, e.guestId, e.fieldName);
        epochSnapshot.set(k, invalidationEpochs.get(k) ?? 0);
      }
      // Chunked sequential POSTs of MAX_BATCH — the backend rejects/slices
      // bigger request lists, so every chunk rides its own call and every
      // queued request resolves from its chunk's response.
      const urls: Record<string, string> = {};
      const failedPairs = new Set<string>();
      for (let i = 0; i < entries.length; i += MAX_BATCH) {
        const chunk = entries.slice(i, i + MAX_BATCH);
        try {
          const result = await fetchAfImageUrls(eventId, chunk);
          if (result && !("isServerError" in result)) {
            Object.assign(urls, result.urls ?? {});
          } else {
            for (const c of chunk) failedPairs.add(`${c.guestId}:${c.fieldName}`);
          }
        } catch {
          // fall through — this chunk's requests resolve null (cells render
          // empty) and get the SHORT error TTL so a retry isn't 280s away.
          for (const c of chunk) failedPairs.add(`${c.guestId}:${c.fieldName}`);
        }
      }
      const now = Date.now();
      for (const r of reqs) {
        const pair = `${r.guestId}:${r.fieldName}`;
        const url = urls[pair] ?? null;
        const k = cacheKey(eventId, r.guestId, r.fieldName);
        // Negative results are cached too (guest has no value) so a long list
        // doesn't re-POST for every empty cell on each re-render; errors get
        // the SHORT TTL instead. An upload landing later shows after the cache
        // expires or after invalidateAfImageUrl() from the uploading surface.
        if ((invalidationEpochs.get(k) ?? 0) === epochSnapshot.get(k)) {
          cache.set(k, {
            url,
            exp: now + (failedPairs.has(pair) ? ERROR_TTL_MS : URL_TTL_MS),
            fetchedAt: now,
          });
        }
        r.resolve(url);
      }
    }),
  );
}

/**
 * Resolve the presigned GET URL for one guest/field, batching all calls of
 * the current microtask tick into a single POST. Returns `null` when the
 * guest has no stored value (or on error).
 */
export function resolveAfImageUrl(
  eventId: string,
  guestId: string,
  fieldName: string,
): Promise<string | null> {
  if (!eventId || !guestId || !fieldName) return Promise.resolve(null);
  const hit = cache.get(cacheKey(eventId, guestId, fieldName));
  if (hit && hit.exp > Date.now()) return Promise.resolve(hit.url);

  return new Promise<string | null>((resolve) => {
    pending.push({ eventId, guestId, fieldName, resolve });
    if (!flushScheduled) {
      flushScheduled = true;
      // Microtask: collects every request queued during the same synchronous
      // render pass, then fires exactly one POST per event.
      queueMicrotask(() => void flush());
    }
  });
}

/**
 * Freshness-sensitive variant for surfaces that are about to DISPLAY the URL
 * for a while (lightbox): a cached URL fetched more than ~250s ago has <30s of
 * presign life left and could expire mid-view — drop it and re-resolve.
 */
export function resolveAfImageUrlFresh(
  eventId: string,
  guestId: string,
  fieldName: string,
): Promise<string | null> {
  const hit = cache.get(cacheKey(eventId, guestId, fieldName));
  if (hit && Date.now() - hit.fetchedAt > FRESH_MAX_AGE_MS) {
    invalidateAfImageUrl(eventId, guestId, fieldName);
  }
  return resolveAfImageUrl(eventId, guestId, fieldName);
}

/** Drop one cached URL — call after an upload/replace/remove so the next
 *  render re-resolves instead of showing the stale (or missing) image. Also
 *  bumps the key's epoch so an in-flight batch can't write the stale URL
 *  back. */
export function invalidateAfImageUrl(
  eventId: string,
  guestId: string,
  fieldName: string,
): void {
  const k = cacheKey(eventId, guestId, fieldName);
  cache.delete(k);
  invalidationEpochs.set(k, (invalidationEpochs.get(k) ?? 0) + 1);
}

/** Test-only: reset module state between cases. */
export function __clearAfImageUrlCacheForTests(): void {
  cache.clear();
  invalidationEpochs.clear();
  pending = [];
  flushScheduled = false;
}
